import { useEffect, useMemo, useState } from 'react';
import { useLocation, NavLink as RouterNavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight, Menu, X, Headset, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { ScrollArea } from '@/Components/ui/ScrollArea';
import { SupportOnlineDialog } from './SupportOnlineDialog';
import { SearchField } from '@/Components/forms/SearchField';
import { buildMenu, type NavItem, type NavChild } from '@/data/menu';
import { normalizePath, pathMatches, itemIsActive, filterMenu } from '@/lib/menuNavigation';
import { useNavigationMenu } from '@/hooks/useNavigationMenu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/Components/ui/Collapsible';
import { cn } from '@/Components/ui/utils';

type AppSidebarProps = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isMobile?: boolean;
  mobileOpen?: boolean;
  onCloseMobile?: () => void;
  onToggleMobile?: () => void;
};

export default function AppSidebar({
  collapsed,
  onToggleCollapsed,
  isMobile = false,
  mobileOpen = false,
  onCloseMobile,
  onToggleMobile,
}: AppSidebarProps) {
  const location = useLocation();
  const path = normalizePath(location.pathname || '/');
  const menu = useNavigationMenu();
  const [filter, setFilter] = useState('');
  const filteredItems = useMemo(() => filterMenu(menu, filter), [menu, filter]);

  const initialOpen = useMemo(() => {
    const open: Record<string, boolean> = {};
    for (const item of menu) {
      if (itemIsActive(item, path) && item.children?.length) {
        open[item.id] = true;
      }
    }
    return open;
  }, [menu, path]);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(initialOpen);

  useEffect(() => {
    setOpenSections((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const item of menu) {
        if (itemIsActive(item, path) && item.children?.length && !next[item.id]) {
          next[item.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [menu, path]);

  const showLabels = !collapsed;

  return (
    <aside
      id="nx-sidebar"
      className={cn(
        'nx-sidebar',
        isMobile && 'nx-sidebar--mobile',
        collapsed && 'nx-sidebar--collapsed',
        isMobile && mobileOpen && 'nx-sidebar--mobile-open',
      )}
      aria-label="Menu lateral"
      aria-hidden={isMobile && !mobileOpen ? true : undefined}
      inert={isMobile && !mobileOpen ? true : undefined}
    >
      <div className="nx-sidebar-top">
        <div className="nx-brand">
          <div className="nx-brand-identity">
            <img src="/logo_infor.png" alt="Infor" className="nx-brand-logo" width={32} height={32} />
            {showLabels && <span className="nx-brand-text">INDUSTRIALIZAÇÃO</span>}
          </div>
          <button
            type="button"
            className="nx-icon-btn nx-icon-btn--ghost nx-sidebar-menu-btn"
            aria-label={
              isMobile ? (mobileOpen ? 'Fechar menu' : 'Abrir menu') : collapsed ? 'Expandir menu' : 'Alternar menu'
            }
            onClick={isMobile ? onToggleMobile : onToggleCollapsed}
          >
            {isMobile && mobileOpen ? <X size={18} strokeWidth={2} /> : <Menu size={18} strokeWidth={2} />}
          </button>
        </div>
        {showLabels && (
          <SearchField className="nx-menu-filter" size="sm" label="Filtrar menu" placeholder="Filtrar menu" value={filter} onChange={(event) => setFilter(event.target.value)} autoComplete="off" />
        )}
      </div>

      <ScrollArea className="nx-sidebar-scroll">
        <nav className="nx-nav" aria-label="Navegação principal">
          {filteredItems.length === 0 && showLabels && <p className="nx-nav-empty">{filter.trim() ? 'Nenhum item encontrado' : 'Nenhum menu disponível'}</p>}
          {filteredItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = Boolean(item.children?.length);
            const isActiveSection = itemIsActive(item, path);

            if (!hasChildren) {
              return (
                <RouterNavLink
                  key={item.id}
                  to={item.href ?? '#'}
                  className={({ isActive }) => cn('nx-nav-item', isActive && 'nx-nav-item--active')}
                  title={collapsed ? item.label : undefined}
                  onClick={isMobile ? onCloseMobile : undefined}
                >
                  <Icon size={17} strokeWidth={1.85} />
                  {showLabels && <span>{item.label}</span>}
                </RouterNavLink>
              );
            }

            if (collapsed) {
              const firstChild = item.children?.[0];
              return (
                <RouterNavLink
                  key={item.id}
                  to={firstChild?.href ?? '#'}
                  className={cn('nx-nav-item', isActiveSection && 'nx-nav-item--active')}
                  title={item.label}
                >
                  <Icon size={17} strokeWidth={1.85} />
                </RouterNavLink>
              );
            }

            const isOpen = Boolean(filter.trim()) || (openSections[item.id] ?? false);

            return (
              <Collapsible
                key={item.id}
                open={isOpen}
                onOpenChange={(open) => {
                  if (!filter.trim()) setOpenSections((prev) => ({ ...prev, [item.id]: open }));
                }}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className={cn('nx-nav-item nx-nav-item--parent', isActiveSection && 'nx-nav-item--section-active')}
                  >
                    <Icon size={17} strokeWidth={1.85} />
                    <span>{item.label}</span>
                    {isOpen ? (
                      <ChevronDown className="nx-nav-chevron" size={15} />
                    ) : (
                      <ChevronRight className="nx-nav-chevron" size={15} />
                    )}
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent forceMount className="nx-nav-children" inert={isOpen ? undefined : true}>
                  <div className="nx-nav-children-inner">
                    <div className="nx-nav-children-body">
                      {item.children?.map((child) => {
                        const active = pathMatches(child.href, path);
                        return (
                          <RouterNavLink
                            key={child.id}
                            to={child.href}
                            className={cn('nx-nav-subitem', active && 'nx-nav-subitem--active')}
                            onClick={isMobile ? onCloseMobile : undefined}
                          >
                            {child.label}
                          </RouterNavLink>
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </nav>
      </ScrollArea>

      <div className="nx-sidebar-footer">
        {showLabels && (
          <SupportOnlineDialog trigger={<button type="button" className="nx-support-card">
            <span className="nx-support-icon" aria-hidden>
              <Headset size={18} strokeWidth={1.9} />
            </span>
            <span>
              <strong>Suporte Online</strong>
              <small>Fale com nossa equipe</small>
            </span>
          </button>} />
        )}
        {!isMobile && (
          <button type="button" className="nx-collapse-toggle" onClick={onToggleCollapsed}>
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
            {showLabels && <span>Recolher menu</span>}
          </button>
        )}
      </div>
    </aside>
  );
}

export type { NavItem, NavChild };
export { buildMenu };
